// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 Frontend — API Service Layer
// Typed wrapper for all Azure Functions endpoints
// Import: import { api } from '@/services/api';
// ═══════════════════════════════════════════════════════════════════════════════

// Always route through the SWA linked-backend proxy (/api/*) for CORS.
//
// NOTE: the proxy also injects x-ms-client-principal, and this file used to
// rely on that as the authentication fallback. PR #630 removed it as an
// authentication source in api/shared/auth.ts (it is unsigned and the Function
// App is separately reachable, so it was forgeable). Authentication is now the
// MSAL bearer token only - see getBearer() in src/lib/auth/msalConfig.ts and
// the bridge in src/lib/auth/bearerBridge.ts.
import { unauthenticatedResponse } from './apiFetch';
import { reasonWarrantsInteraction } from '../lib/auth/bearerBridge';

const BASE_URL = '/api';

// ─── HTTP Client ───
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  // Attach the MSAL Bearer token when available — lazy import avoids circular
  // deps and keeps the module usable outside of the React tree.
  let authHeader: Record<string, string> = {};
  let haveBearer = false;
  try {
    const { getBearer, startInteractiveAuth } = await import(
      '../lib/auth/msalConfig'
    );
    const { token, reason } = await getBearer();
    if (token) {
      authHeader = { Authorization: `Bearer ${token}` };
      haveBearer = true;
    } else {
      // Escalate only for a reason that actually calls for interaction. The
      // bridge's verdict is authoritative: identity-unknown in particular must
      // NOT redirect, because we cannot say who to sign in.
      if (reasonWarrantsInteraction(reason)) void startInteractiveAuth(reason);
    }
  } catch {
    // MSAL module unavailable (not configured, or outside a browser).
  }

  // A caller that supplies its own Authorization header is already authorised.
  if (!haveBearer) {
    try {
      haveBearer = new Headers(options.headers as HeadersInit | undefined).has('Authorization');
    } catch {
      haveBearer = false;
    }
  }

  // Client-generated request id — written to a header so the server can log
  // it alongside its work. Surfaces in error messages so an analyst can paste
  // it back to us and we can grep App Insights for that exact call.
  const clientRequestId = `c${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

  // Do not send a staff-gated call with no Authorization header.
  //
  // Before PR #630 such a request was still authenticated, by the
  // x-ms-client-principal header the SWA proxy injects. That path is gone, so
  // an unauthenticated call can only come back 401. This is the path the
  // handoff surface actually uses - api.handoffs.list/create/update/delete all
  // route through here, NOT through apiFetch - so the short-circuit has to
  // exist in both places or the fix only covers half the app.
  //
  // The synthetic response flows into the normal `!res.ok` handling below, so
  // callers get the usual ApiError with status 401 and code AUTH_UNAUTHORIZED,
  // indistinguishable from the server's own rejection.
  const res = haveBearer
    ? await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'x-client-request-id': clientRequestId,
          ...authHeader,
          ...options.headers,
        },
      })
    : unauthenticatedResponse();

  if (!res.ok) {
    // 401 AFTER we presented a credential = the API rejected it. That is the
    // one case where re-authenticating interactively is the right answer.
    //
    // It is deliberately gated on haveBearer. When no bearer was available the
    // 401 below is OUR OWN synthetic response, and the bridge has already made
    // the escalation decision for that case with far more information than we
    // have here - including the identity-unknown verdict, where it declines to
    // redirect precisely because we cannot say who to sign in. Escalating here
    // on a synthetic 401 would silently override that and send a user whose
    // identity probe merely blipped into a full-page redirect.
    //
    // The redirect goes to MSAL, not `/.auth/login/aad`. That endpoint
    // re-establishes the SWA session, which since PR #630 the API no longer
    // accepts - so bouncing through it returned the user to the same 401, and
    // the sessionStorage guard then suppressed any further attempt.
    if (res.status === 401 && haveBearer) {
      try {
        const { startInteractiveAuth } = await import('../lib/auth/msalConfig');
        void startInteractiveAuth('interaction-required');
      } catch {
        // MSAL unavailable - fall through to the normal error path below.
      }
    }
    // Read body as text first — that way an HTML error page, empty body, or
    // truncated response is preserved verbatim instead of getting silently
    // dropped when .json() fails. We attempt JSON parse on the captured text
    // and only fall through to the raw text if it isn't structured.
    const rawText = await res.text().catch(() => '');
    let body: any = null;
    if (rawText) {
      try { body = JSON.parse(rawText); } catch { /* not JSON — keep rawText as fallback */ }
    }
    // Correlation IDs — stored on the error for App Insights lookup / support triage,
    // but NEVER appended to the user-visible message string.
    const serverRequestId = res.headers.get('x-azure-ref') || res.headers.get('x-ms-request-id') || res.headers.get('request-id') || '';
    const requestId = serverRequestId
      ? `req=${serverRequestId} · client=${clientRequestId}`
      : `client=${clientRequestId}`;
    // Handle structured error: { error: { code, message, detail, status } }
    if (body?.error && typeof body.error === 'object') {
      const msg = body.error.message || res.statusText || `HTTP ${res.status}`;
      const detail = body.error.detail || (rawText && rawText !== JSON.stringify(body) ? rawText.slice(0, 500) : undefined);
      throw new ApiError(res.status, msg, body.error.code, detail, requestId);
    }
    // Legacy flat error: { error: "some string" }
    // NOTE: use `||`, not `??`, so an EMPTY string falls through. Over HTTP/2
    // (Azure SWA / Front Door always negotiate h2) `res.statusText` is mandated
    // to be '' by the Fetch spec, and `??` only short-circuits on null/undefined
    // — so a `?? res.statusText` chain would resolve to '' on any empty-body
    // error (e.g. a 404 for an undeployed route), producing a blank ApiError
    // message that drops the status code. Always end on a non-empty `HTTP <n>`.
    const message =
      (typeof body?.error === 'string' ? body.error : '')
      || (rawText ? rawText.slice(0, 500) : '')  // raw HTML/text body — better than nothing
      || res.statusText
      || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, undefined, undefined, requestId);
  }

  // Clear the interactive-sign-in guard on any successful response.
  //
  // The old `auth-redirect-in-progress` key guarded a redirect to
  // /.auth/login/aad, which is no longer how we re-authenticate. Clearing it
  // too keeps a stale key from a pre-upgrade session from lingering.
  if (typeof window !== 'undefined') {
    try {
      const { noteBearerAcquired } = await import('../lib/auth/msalConfig');
      noteBearerAcquired();
    } catch { /* MSAL unavailable - nothing to re-arm */ }
    sessionStorage.removeItem('auth-redirect-in-progress');
  }

  return res.json();
}

function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, signal ? { signal } : undefined);
}

function post<T>(path: string, body?: any): Promise<T> {
  return request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
}

function patch<T>(path: string, body: any): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

function put<T>(path: string, body: any): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

/** Upload files via FormData — skips Content-Type so the browser sets the multipart boundary. */
async function uploadForm<T>(path: string, formData: FormData): Promise<T> {
  let authHeader: Record<string, string> = {};
  let haveBearer = false;
  try {
    const { getBearer, startInteractiveAuth } = await import(
      '../lib/auth/msalConfig'
    );
    const { token, reason } = await getBearer();
    if (token) {
      authHeader = { Authorization: `Bearer ${token}` };
      haveBearer = true;
    } else {
      if (reasonWarrantsInteraction(reason)) void startInteractiveAuth(reason);
    }
  } catch { /* MSAL not available */ }

  // Same rule as request<T>() - an upload is staff-gated too, and sending it
  // without a bearer can only 401 while consuming the user's file.
  const res = haveBearer
    ? await fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        headers: { ...authHeader },
        body: formData,
      })
    : unauthenticatedResponse();

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (body?.error && typeof body.error === 'object') {
      throw new ApiError(res.status, body.error.message || 'Upload failed', body.error.code, body.error.detail);
    }
    const message = (typeof body?.error === 'string' ? body.error : null) ?? res.statusText ?? 'Upload failed';
    throw new ApiError(res.status, message);
  }

  return res.json();
}

export class ApiError extends Error {
  /** Machine-readable error code, e.g. "KB_IMPORT_NO_FILES" */
  code?: string;
  /** Raw detail / stack trace from the server */
  detail?: string;
  /** Correlation IDs (client + Azure request IDs) for App Insights lookup — never render to users */
  requestId?: string;

  constructor(public status: number, message: string, code?: string, detail?: string, requestId?: string) {
    super(message);
    this.code = code;
    this.detail = detail;
    this.requestId = requestId;
  }
}

// ─── IR Agent streaming protocol ─────────────────────────────────────────
// Mirrors the FoundryStreamEvent shape on the backend, plus the metadata
// the function adds (agent, conversationId on the completed event).
export type IRAgentToolCall = { type: string; name?: string; arguments?: string };
export type IRAgentTokenUsage = { inputTokens: number; outputTokens: number; totalTokens: number };
export type IRAgentStreamEvent =
  | { type: 'created'; conversationId: string; rotated: boolean; rotationReason?: 'stale' | 'context-overflow' | 'timeout' }
  | { type: 'lock-retry'; attempt: number; waitMs: number }
  | { type: 'delta'; text: string }
  | { type: 'tool_call'; toolCall: IRAgentToolCall }
  | { type: 'completed'; conversationId?: string; responseId: string; status: string; reply: string; toolCalls: IRAgentToolCall[]; usage?: IRAgentTokenUsage; agent?: { name: string; version: string } }
  | { type: 'ping'; t?: number }
  | { type: 'error'; code: string; message: string; status?: number; clientRequestId?: string };

async function* streamIRAgentChat(
  params: {
    caseId?: string;
    sessionId?: string;
    message: string;
    conversationId?: string;
    context?: { caseNumber?: string; title?: string; customerName?: string };
    surface?: 'engineering' | 'ir' | 'threatHunting';
  },
  signal?: AbortSignal,
): AsyncIterable<IRAgentStreamEvent> {
  let authHeader: Record<string, string> = {};
  try {
    const { getAccessToken } = await import('../lib/auth/msalConfig');
    const token = await getAccessToken();
    if (token) authHeader = { Authorization: `Bearer ${token}` };
  } catch { /* no auth — SWA proxy may inject x-ms-client-principal */ }

  const clientRequestId = `c${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const res = await fetch(`${BASE_URL}/ir-agent/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'x-client-request-id': clientRequestId,
      ...authHeader,
    },
    body: JSON.stringify(params),
    signal,
  });

  if (!res.ok) {
    // Pre-stream failure (auth, validation, handler crash before headers)
    // — preserve the same error semantics as the JSON path.
    const rawText = await res.text().catch(() => '');
    let body: any = null;
    if (rawText) { try { body = JSON.parse(rawText); } catch { /* not JSON */ } }
    const serverRequestId = res.headers.get('x-azure-ref') || res.headers.get('x-ms-request-id') || res.headers.get('request-id') || '';
    const requestId = serverRequestId ? `req=${serverRequestId} · client=${clientRequestId}` : `client=${clientRequestId}`;
    if (body?.error && typeof body.error === 'object') {
      throw new ApiError(res.status, body.error.message || 'Request failed', body.error.code, body.error.detail, requestId);
    }
    throw new ApiError(res.status, (typeof body?.error === 'string' ? body.error : null) ?? rawText.slice(0, 500) ?? res.statusText ?? 'Request failed', undefined, undefined, requestId);
  }
  if (!res.body) throw new ApiError(0, 'Streaming response had no body', undefined, undefined, `client=${clientRequestId}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        let evtName: string | undefined;
        const dataLines: string[] = [];
        for (const line of raw.split('\n')) {
          if (line.startsWith('event:')) evtName = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        }
        if (!evtName || dataLines.length === 0) continue;
        let payload: any;
        try { payload = JSON.parse(dataLines.join('\n')); } catch { continue; }
        // Heartbeats are intentionally surfaced — UI may want to show "still
        // working…" beat counters without parsing the inner content.
        yield { type: evtName as any, ...payload };
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* already released */ }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// API CLIENT
// ═══════════════════════════════════════════════════════════════════════════════
// ── QA query helper ──────────────────────────────────────────────────────
// Builds a query string for QA endpoints, dropping empty/undefined values so
// we don't end up with `/qa-scores?analystId=` (which the API would interpret
// as "filter to row where analystId is empty string").
function qaQuery(params?: { analystId?: string; periodStart?: string; scope?: 'self' | 'team' }): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  if (params.analystId) qs.set('analystId', params.analystId);
  if (params.periodStart) qs.set('periodStart', params.periodStart);
  if (params.scope) qs.set('scope', params.scope);
  return qs.toString();
}

// ─── IOC watchlist backfill (admin) ─────────────────────────────────────────
export type IocBackfillVerdict = 'lost-update-drop' | 'extraction-failure' | 'mixed' | 'no-drops';

/** merge = additive-only; rebuild = also prunes entries the current extractor
 *  no longer promotes (stale reporting-source links, mistyped types). */
export type IocBackfillMode = 'merge' | 'rebuild';

export interface IocBackfillArticle {
  id: string;
  title: string;
  extractedIocCount: number;
  contentLength: number;
  fileExt: string;
  error?: string;
}

export interface IocBackfillWeek {
  weekKey: string;
  articleCount: number;
  totalIocsBefore: number;
  totalIocsAfter: number;
  droppedFromRace: number;
  /** rebuild mode only: stale entries pruned. Optional — older API builds omit it. */
  removedStale?: number;
  extractionFailures: number;
  articles: IocBackfillArticle[];
}

export interface IocBackfillResult {
  kbId: string;
  kbName: string;
  /** Optional — older API builds omit it (implicitly 'merge'). */
  mode?: IocBackfillMode;
  range: { since: string; until: string };
  weeks: IocBackfillWeek[];
  verdict: IocBackfillVerdict;
  applied: boolean;
  appliedAt?: string;
  summary: {
    totalArticles: number;
    totalIocsExtracted: number;
    totalIocsDeduped: number;
    totalIocsAddedToWatchlist: number;
    /** rebuild mode only. Optional — older API builds omit it. */
    totalIocsRemovedFromWatchlist?: number;
  };
}

// ─── Test customer-portal seed / cleanup (Admin → Test Data) ───
export interface SeedTestCustomerPortalsResult {
  created: string[];
  updated: string[];
  skipped: Array<{ slug: string; reason: string }>;
}

export interface CleanupTestCustomerPortalsResult {
  deleted: string[];
  accessGrantsRemoved: number;
  accessRequestsRemoved: number;
  skipped: Array<{ slug: string; reason: string }>;
}

export const api = {

  // ─── Me (user profile sync) ───
  me: {
    /** Fetch (or create on first login) the caller's full profile from Cosmos.
     *  Falls back gracefully — callers should use MSAL AccountInfo if this throws. */
    get: () => get<UserProfile>('/me'),
    /** Persist mutable user preferences (shift; tier for admin/lead). */
    updateProfile: (data: { shift?: string; tier?: string }) => patch<UserProfile>('/me', data),
    updatePrefs: (prefs: any) => patch<UserProfile>('/me', { notificationPrefs: prefs }),
  },

  // ─── Users (admin user management) ───
  users: {
    /** List all registered user profiles (admin only). */
    list: () => get<UserProfile[]>('/users'),
    /** Slim directory available to every authenticated user — used to
     *  resolve display names anywhere a UPN/OID appears in the UI. */
    directory: () => get<UserDirectoryEntry[]>('/users/directory'),
    /** Lazy single-user display-name resolution (Cosmos → Microsoft Graph).
     *  Used as a fallback when an id is absent from the slim directory.
     *  `resolved:false` means even Graph could not name the user. */
    resolveName: (id: string) =>
      get<{ id: string; displayName: string; resolved: boolean }>(
        `/users/resolve/${encodeURIComponent(id)}`,
      ),
    /** Re-resolve every missing/UPN-shaped/local-part-placeholder displayName
     *  in the users container via Microsoft Graph (admin only). */
    repairNames: () =>
      post<{
        scanned: number;
        clean: number;
        repaired: number;
        unresolvedCount: number;
        repairs: Array<{ id: string; email: string; from: string; to: string }>;
        unresolved: Array<{ id: string; email: string; displayName: string }>;
      }>('/users/repair-names'),
    /** Update a user's tier, shift, or role (admin only). */
    update: (id: string, data: { tier?: string; shift?: string; role?: string }) => patch<any>(`/users/${id}`, data),
    /** Remove a user profile by Entra OID (admin only). */
    remove: (id: string) => del<{ ok: boolean }>(`/users/${id}`),
  },

  // ─── Admin maintenance / diagnostics ───
  admin: {
    /**
     * Run the IOC watchlist backfill + diagnostic against a Threat Intel KB.
     * `apply:false` is a dry-run preview (no writes); `apply:true` performs the
     * merge via the production sync path. Admin-role JWT only (server rejects
     * API-key callers with 403).
     */
    backfillIocWatchlist: (params: { kbId?: string; since: string; until?: string; apply: boolean; mode?: IocBackfillMode }) =>
      post<IocBackfillResult>('/ioc-maintenance/backfill', params),

    /**
     * Seed the three FICTIONAL test customer portals (Kahs Defense, Globex,
     * Wayne). Idempotent upsert; refuses to overwrite any non-test doc.
     * Server 404s unless CUSTOMER_PORTAL_ENABLED is on. Admin-role only.
     */
    seedTestCustomerPortals: () =>
      post<SeedTestCustomerPortalsResult>('/test-data/seed-customer-portals'),

    /**
     * Remove the three test customer portals plus any access grants / pending
     * access requests pointing at them. Server 404s unless the portal flag is
     * on. Admin-role only.
     */
    cleanupTestCustomerPortals: () =>
      post<CleanupTestCustomerPortalsResult>('/test-data/cleanup-customer-portals'),
  },

  // ─── Knowledge Bases ───
  kbs: {
    list: () => get<KnowledgeBase[]>('/kbs'),
    create: (data: Partial<KnowledgeBase>) => post<KnowledgeBase>('/kbs', data),
    update: (id: string, data: Partial<KnowledgeBase>) => patch<KnowledgeBase>(`/kbs/${id}`, data),
    delete: (id: string) => del<{ ok: boolean }>(`/kbs/${id}`),
    seedCategories: (id: string) => post<any[]>(`/kbs/${id}/seed-categories`),
  },

  // ─── Articles ───
  articles: {
    list: (params?: { categoryId?: string; status?: string; tag?: string; kbId?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return get<Article[]>(`/articles${qs ? '?' + qs : ''}`);
    },
    listSummaries: (params?: { categoryId?: string; status?: string; tag?: string; kbId?: string; limit?: number; offset?: number }) => {
      const qs = params ? new URLSearchParams(params as any).toString() : '';
      // NOTE: top-level path on purpose. The previous nested route
      // /articles/summaries was being matched by /articles/{id} in the Azure
      // Functions runtime (id="summaries" → 404 "Article not found"), which
      // silently emptied every KB for every user. Keep this path flat.
      return get<any[]>(`/articles-summaries${qs ? '?' + qs : ''}`);
    },
    get: (id: string) => get<Article>(`/articles/${id}`),
    create: (data: Partial<Article>) => post<Article>('/articles', data),
    update: (id: string, data: Partial<Article> & { versionNote?: string }) =>
      patch<Article>(`/articles/${id}`, data),
    verify: (id: string) => post<Article>(`/articles/${id}/verify`),
    bulkVerify: (articleIds: string[]) => post<{ verified: number }>('/articles/bulk-verify', { articleIds }),
    feedback: (id: string, vote: 'up' | 'down') =>
      post<{ up: number; down: number }>(`/articles/${id}/feedback`, { vote }),
    delete: (id: string) => del<{ ok: boolean }>(`/articles/${id}`),
    addComment: (articleId: string, content: string) =>
      post<{ id: string }>(`/articles/${articleId}/comments`, { content }),
    deleteComment: (articleId: string, commentId: string) =>
      del<{ ok: boolean }>(`/articles/${articleId}/comments/${commentId}`),
    bulkMove: (articleIds: string[], targetCategoryId: string) =>
      post<{ moved: Article[]; failed: string[]; movedCount: number; failedCount: number }>(
        '/articles/bulk-move', { articleIds, targetCategoryId }),
  },

  // ─── Search ───
  search: {
    query: (q: string, tags?: string[], kbId?: string) => {
      const params = new URLSearchParams({ q });
      if (tags?.length) params.set('tags', tags.join(','));
      if (kbId) params.set('kbId', kbId);
      return get<Article[]>(`/search?${params}`);
    },
    /**
     * KB-scoped Azure AI Search. Returns summaries-shaped results with
     * optional `snippet` and `score` fields for ranking display. Empty
     * results for q shorter than 2 chars (the endpoint short-circuits).
     */
    articles: (params: { q: string; kbId?: string; tags?: string[]; limit?: number; signal?: AbortSignal }) => {
      const qs = new URLSearchParams({ q: params.q });
      if (params.kbId) qs.set('kbId', params.kbId);
      if (params.tags?.length) qs.set('tags', params.tags.join(','));
      if (params.limit) qs.set('limit', String(params.limit));
      return get<Array<Article & { snippet?: string; score?: number }>>(`/articles-search?${qs}`, params.signal);
    },
    /**
     * Batch up to 50 search queries in a single round-trip. Identical queries
     * are de-duplicated server-side. Returns `{ [key]: hits[] }` so callers
     * can correlate results back to their original key (e.g. an escalation id).
     */
    batch: (queries: Array<{ key: string; q: string; tags?: string[]; kbId?: string | null }>) =>
      post<{ results: Record<string, Article[]> }>('/search/batch', { queries }),
  },

  // ─── Categories ───
  categories: {
    list: (kbId?: string) => {
      const qs = kbId ? `?kbId=${kbId}` : '';
      return get<any[]>(`/categories${qs}`);
    },
    create: (data: Record<string, any>) => post<any>('/categories', data),
    update: (id: string, data: Record<string, any>) => patch<any>(`/categories/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/categories/${id}`),
  },

  // ─── KB Document Uploads ───
  kbUpload: {
    /** Upload document files (DOCX, PDF, etc.) to a KB category. */
    upload: (files: File[], kbId: string, categoryId: string) => {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      return uploadForm<{
        uploaded: number;
        rejected: number;
        results: Array<{ filename: string; status: 'uploaded' | 'rejected'; articleId?: string; reason?: string; blobUrl?: string }>;
      }>(`/kb/upload?kbId=${encodeURIComponent(kbId)}&categoryId=${encodeURIComponent(categoryId)}`, fd);
    },
  },

  // ─── Training ───
  training: {
    /** Course catalog — SCORM courses stored in the `courses` container. */
    courses: () => get<import('../types').Course[]>('/training/courses'),
    /** Edit course metadata (required, category, renewal, cosmetics). */
    updateCourse: (id: string, data: Partial<import('../types').Course>) =>
      patch<import('../types').Course>(`/training/courses/${encodeURIComponent(id)}`, data),
    /** Upload a SCORM .zip plus the client-parsed course metadata. */
    uploadScorm: (file: File, course: import('../types').Course) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('course', JSON.stringify(course));
      return uploadForm<import('../types').Course>('/training/scorm-upload', fd);
    },
    /**
     * Built-in SC-200 prep course. Learner-safe metadata only: the module tree
     * carries no correctness field, and the practice-exam start payload carries
     * no answer key or rationale. Both are enforced server-side and contract
     * tested in api/tests/sc200Exam.test.ts.
     */
    sc200: {
      course: () => get<import('../types/sc200').Sc200CourseDetail>('/training/sc200/course'),
      /**
       * Patriot-authored lesson body plus any hands-on simulations for one
       * module. Formative and unscored throughout - nothing in this payload is
       * graded or written to training progress.
       */
      module: (moduleId: string) =>
        get<import('../types/sc200').Sc200ModulePayload>(`/training/sc200/module/${encodeURIComponent(moduleId)}`),
      startPractice: (mode: 'practice' | 'exam', size: number) =>
        post<import('../types/sc200').Sc200StartPayload>('/training/sc200/practice/start', { mode, size }),
      submitPractice: (body: {
        attemptId: string; mode: 'practice' | 'exam'; size: number;
        responses: { itemId: string; selected: number[] }[];
      }) => post<import('../types/sc200').Sc200SubmitPayload>('/training/sc200/practice/submit', body),
    },
    myProgress: () => get<TrainingProgress[]>('/training/progress'),
    allProgress: (courseId?: string) => {
      const qs = courseId ? `?courseId=${courseId}` : '';
      return get<TrainingProgress[]>(`/training/progress/all${qs}`);
    },
    completeModule: (data: {
      courseId: string;
      moduleId: string;
      score: number | null;
      timeSpentMinutes: number;
      scormData: Record<string, string>;
    }) => post<TrainingProgress>('/training/scorm-complete', data),
    saveBookmark: (data: {
      courseId: string;
      moduleId: string;
      bookmark: string;
      scormData: Record<string, string>;
    }) => post<TrainingProgress>('/training/scorm-bookmark', data),
    compliance: () => get<ComplianceReport>('/training/compliance'),
    generateCertificate: (courseId: string) =>
      post<Certificate>('/training/certificate', { courseId }),
    /** Save the caller's personal notes for a course (upserts progress). */
    saveNotes: (courseId: string, notes: string) =>
      post<TrainingProgress>('/training/notes', { courseId, notes }),
    /** Email the caller their saved notes. 503 = mail not configured. */
    emailNotes: (courseId: string) =>
      post<{ ok: boolean; sentTo: string }>('/training/notes/email', { courseId }),
    /** URL for a file inside an extracted SCORM package — used as the
     *  player iframe src (same-origin, so SWA auth cookies apply). */
    scormContentUrl: (courseId: string, path: string) =>
      `/api/training/scorm-content/${encodeURIComponent(courseId)}/${path.split('/').map(encodeURIComponent).join('/')}`,
    // ── Assignments ──
    assignments: () => get<import('../types').TrainingAssignment[]>('/training/assignments'),
    createAssignment: (data: Partial<import('../types').TrainingAssignment>) =>
      post<import('../types').TrainingAssignment>('/training/assignments', data),
    updateAssignment: (id: string, data: Partial<import('../types').TrainingAssignment>) =>
      patch<import('../types').TrainingAssignment>(`/training/assignments/${id}`, data),
    deleteAssignment: (id: string) => del<{ ok: boolean }>(`/training/assignments/${id}`),
    // ── Groups ──
    groups: () => get<import('../types').TrainingGroup[]>('/training/groups'),
    createGroup: (data: Partial<import('../types').TrainingGroup>) =>
      post<import('../types').TrainingGroup>('/training/groups', data),
    updateGroup: (id: string, data: Partial<import('../types').TrainingGroup>) =>
      patch<import('../types').TrainingGroup>(`/training/groups/${id}`, data),
    deleteGroup: (id: string) => del<{ ok: boolean; removedAssignments: number }>(`/training/groups/${id}`),
    // ── Remedial rule ──
    remedialRule: () => get<import('../types').RemedialRule>('/training/remedial-rule'),
    saveRemedialRule: (rule: import('../types').RemedialRule) =>
      put<import('../types').RemedialRule>('/training/remedial-rule', rule),
  },

  // ─── Projects ───
  projects: {
    list: (status?: string) => {
      const qs = status ? `?status=${status}` : '';
      return get<Project[]>(`/projects${qs}`);
    },
    get: (id: string) => get<Project>(`/projects/${id}`),
    create: (data: Partial<Project>) => post<Project>('/projects', data),
    update: (id: string, data: Partial<Project>) => patch<Project>(`/projects/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/projects/${id}`),
  },

  // ─── Project Columns (Kanban override) ───
  // The Kanban derives its columns synchronously on the client from
  // src/config/projectColumns.ts. This endpoint only persists an optional
  // admin override list — a plain string[] under the existing `config`
  // container. When null, the client uses its hardcoded defaults.
  projectColumns: {
    list: () => get<{ columns: string[] | null }>('/project-columns'),
    save: (columns: string[]) =>
      put<{ columns: string[] }>('/project-columns', { columns }),
  },

  // ─── Tasks ───
  tasks: {
    list: (projectId: string, params?: { status?: string; assigneeId?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return get<Task[]>(`/projects/${projectId}/tasks${qs ? '?' + qs : ''}`);
    },
    my: (status?: 'open' | 'done') => {
      const qs = status ? `?status=${status}` : '';
      return get<Task[]>(`/tasks/my${qs}`);
    },
    board: (params: import('../lib/projectTaskPages').TaskBoardOptions) => {
      const qs = new URLSearchParams({ pagination: params.pagination, includeDone: String(params.includeDone), includeUnsupported: String(params.includeUnsupported), includeIdentityIssues: String(params.includeIdentityIssues), pageSize: String(params.pageSize) });
      if (params.continuationToken) qs.set('continuationToken', params.continuationToken);
      return get<import('../lib/projectTaskPages').TaskBoardPage>(`/tasks/board?${qs}`);
    },
    create: (projectId: string, data: Partial<Task>) =>
      post<Task>(`/projects/${projectId}/tasks`, data),
    /** Create a standalone task — one not associated with any project. The
     *  server writes `projectId: null` (queries discover it via
     *  `NOT IS_DEFINED(c.projectId) OR c.projectId = null`). */
    createStandalone: (data: Partial<Task>) => post<Task>(`/tasks`, data),
    update: (id: string, data: Partial<Task>) => patch<Task>(`/tasks/${id}`, data),
    cycleStatus: (id: string) => post<Task>(`/tasks/${id}/status-cycle`),
    workload: () => get<Record<string, { open: number; done: number; overdue: number; blocked: number }>>('/tasks/workload'),
    remove: (id: string) => del<{ ok: boolean }>(`/tasks/${id}`),
  },

  // ─── Comments ───
  comments: {
    list: (taskId: string) => get<Comment[]>(`/tasks/${taskId}/comments`),
    create: (taskId: string, content: string) =>
      post<Comment>(`/tasks/${taskId}/comments`, { content }),
  },

  // ─── Sprints ───
  sprints: {
    list: (projectId: string, status?: string) => {
      const qs = status ? `?status=${status}` : '';
      return get<any[]>(`/projects/${projectId}/sprints${qs}`);
    },
    create: (projectId: string, data: any) => post<any>(`/projects/${projectId}/sprints`, data),
    update: (id: string, data: any) => patch<any>(`/sprints/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/sprints/${id}`),
    burndown: (id: string) => get<any>(`/sprints/${id}/burndown`),
  },

  // ─── Project Goals / OKRs ───
  goals: {
    list: (params?: { ownerId?: string; status?: string }) => {
      const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
      return get<any[]>(`/project-goals${qs}`);
    },
    create: (data: any) => post<any>('/project-goals', data),
    update: (id: string, data: any) => patch<any>(`/project-goals/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/project-goals/${id}`),
  },

  // ─── Project Templates ───
  projectTemplates: {
    list: () => get<any[]>('/project-templates'),
    create: (data: any) => post<any>('/project-templates', data),
    remove: (id: string) => del<{ ok: boolean }>(`/project-templates/${id}`),
    fromTemplate: (data: { templateId: string; name?: string; dueDate?: string }) =>
      post<any>('/projects/from-template', data),
  },

  // ─── Time Tracking ───
  taskTime: {
    log: (taskId: string, data: { minutes: number; billable?: boolean; note?: string; workDate?: string }) =>
      post<any>(`/tasks/${taskId}/time`, data),
    remove: (taskId: string, entryId: string) =>
      del<{ ok: boolean }>(`/tasks/${taskId}/time/${entryId}`),
    report: (params?: { from?: string; to?: string }) => {
      const qs = params ? '?' + new URLSearchParams(params as any).toString() : '';
      return get<any>(`/tasks/time-report${qs}`);
    },
  },

  // ─── Task Checklists (per-task, distinct from daily SOC checklist) ───
  taskChecklist: {
    add: (taskId: string, text: string) =>
      post<any>(`/tasks/${taskId}/checklist`, { text }),
    toggle: (taskId: string, itemId: string, body: { done?: boolean; text?: string }) =>
      patch<any>(`/tasks/${taskId}/checklist/${itemId}`, body),
    remove: (taskId: string, itemId: string) =>
      del<{ ok: boolean }>(`/tasks/${taskId}/checklist/${itemId}`),
  },

  // ─── Dependencies / Watchers / Status Updates ───
  taskAdv: {
    setDependencies: (taskId: string, blockedByIds: string[]) =>
      post<any>(`/tasks/${taskId}/dependencies`, { blockedByIds }),
    watch: (taskId: string, userId?: string) =>
      post<any>(`/tasks/${taskId}/watch`, { userId }),
    unwatch: (taskId: string, userId?: string) => {
      const qs = userId ? `?userId=${userId}` : '';
      return del<any>(`/tasks/${taskId}/watch${qs}`);
    },
    bulk: (taskIds: string[], action?: 'archive' | 'restore' | 'delete', updates?: any) =>
      post<any>('/tasks/bulk', { taskIds, action, updates }),
    workloadEnhanced: (from?: string) =>
      get<Record<string, any>>(`/tasks/workload-enhanced${from ? `?from=${from}` : ''}`),
  },

  projectAdv: {
    addStatusUpdate: (projectId: string, data: { health?: string; summary: string; detail?: string }) =>
      post<any>(`/projects/${projectId}/status-updates`, data),
    deleteStatusUpdate: (projectId: string, updateId: string) =>
      del<{ ok: boolean }>(`/projects/${projectId}/status-updates/${updateId}`),
    portfolio: () => get<any[]>('/projects/portfolio'),
  },

  // ─── Saved Project Views ───
  projectViews: {
    list: () => get<any[]>('/project-views'),
    create: (data: any) => post<any>('/project-views', data),
    update: (id: string, data: any) => patch<any>(`/project-views/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/project-views/${id}`),
  },

  // ─── Assistant ───
  assistant: {
    chat: (message: string, conversationHistory?: Array<{ role: string; content: string }>) =>
      post<{ response: string; sources: any[]; hasResults: boolean }>(
        '/assistant/chat', { message, conversationHistory }),
    /**
     * Streaming chat: keeps the connection alive with SSE heartbeats so the
     * agent's multi-step investigation isn't cut off by the gateway idle
     * timeout. Calls onStatus with progress labels as the agent works, and
     * resolves with the final answer. Throws on error events / pre-stream
     * failures so callers can fall back to the local KB summary.
     */
    chatStream: async (
      message: string,
      conversationHistory: Array<{ role: string; content: string }> | undefined,
      onStatus: (label: string) => void,
      signal?: AbortSignal,
    ): Promise<{ response: string; sources: any[]; hasResults: boolean }> => {
      let authHeader: Record<string, string> = {};
      try {
        const { getAccessToken } = await import('../lib/auth/msalConfig');
        const token = await getAccessToken();
        if (token) authHeader = { Authorization: `Bearer ${token}` };
      } catch { /* SWA proxy injects x-ms-client-principal */ }

      const res = await fetch(`${BASE_URL}/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream', ...authHeader },
        body: JSON.stringify({ message, conversationHistory }),
        signal,
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        throw new ApiError(res.status || 0, text.slice(0, 300) || res.statusText || 'Assistant request failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let result: { response: string; sources: any[]; hasResults: boolean } | null = null;
      let streamErr: string | null = null;

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            let evtName: string | undefined;
            const dataLines: string[] = [];
            for (const line of raw.split('\n')) {
              if (line.startsWith('event:')) evtName = line.slice(6).trim();
              else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
            }
            if (!evtName || dataLines.length === 0) continue;
            let payload: any;
            try { payload = JSON.parse(dataLines.join('\n')); } catch { continue; }
            if (evtName === 'status') onStatus(payload.label || '');
            else if (evtName === 'done') result = { response: payload.response, sources: payload.sources || [], hasResults: !!payload.hasResults };
            else if (evtName === 'error') streamErr = payload.message || 'Assistant error';
            // 'ping' is just keepalive — ignore.
          }
        }
      } finally {
        try { reader.releaseLock(); } catch { /* already released */ }
      }

      if (streamErr) throw new ApiError(503, streamErr);
      if (!result) throw new ApiError(0, 'Assistant stream ended without a result');
      return result;
    },
    gaps: () => get<SearchGap[]>('/assistant/gaps'),
    resolveGap: (id: string, articleId: string) =>
      post<SearchGap>(`/assistant/gaps/${id}/resolve`, { articleId }),
    analyzeIR: (irCase: unknown) =>
      post<{ analysis: import('../types').IRAIAnalysis; provider: string; model: string; usage?: { inputTokens: number; outputTokens: number } }>(
        '/assistant/analyze-ir', { irCase }),
    irAgentChat: (params: {
      caseId?: string;
      sessionId?: string;
      message: string;
      conversationId?: string;
      context?: { caseNumber?: string; title?: string; customerName?: string };
      /** Surface discriminator — when 'engineering', the backend uses the
       *  Engineering agent identity (env: FOUNDRY_ENGINEERING_AGENT_NAME) and
       *  gates on the 'engineering' RBAC view. Defaults derived from caseId. */
      surface?: 'engineering' | 'ir' | 'threatHunting';
    }) =>
      post<{
        conversationId: string;
        responseId: string;
        status: string;
        reply: string;
        toolCalls: Array<{ type: string; name?: string; arguments?: string }>;
        usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
        rotated?: boolean;
        rotationReason?: 'stale' | 'context-overflow' | 'timeout';
        lockRetries?: number;
        agent: { name: string; version: string };
      }>('/ir-agent/chat', params),
    /** Streaming variant of irAgentChat. Returns an async iterable of events
     *  the caller pumps into UI state. Defeats Azure Front Door's 60s idle
     *  cutoff (the function emits SSE heartbeats while the agent thinks) and
     *  shows partial text as the agent generates it. */
    irAgentChatStream: (
      params: {
        caseId?: string;
        sessionId?: string;
        message: string;
        conversationId?: string;
        context?: { caseNumber?: string; title?: string; customerName?: string };
        /** Surface discriminator — see `irAgentChat`. */
        surface?: 'engineering' | 'ir' | 'threatHunting';
      },
      signal?: AbortSignal,
    ): AsyncIterable<IRAgentStreamEvent> => streamIRAgentChat(params, signal),
    irAgentSetConversationMetadata: (conversationId: string, metadata: Record<string, string>) =>
      post<{ ok: boolean }>(
        `/ir-agent/conversations/${encodeURIComponent(conversationId)}/metadata`,
        { metadata },
      ),
    irAgentHealth: () =>
      get<{
        ok: boolean;
        envVars: Record<string, boolean>;
        agent?: { name: string; version: string };
        endpoint?: string;
        credentialMode: 'service-principal' | 'managed-identity-pinned' | 'managed-identity-default';
        cachedScope: string | null;
        attempts: Array<{
          scope: string;
          tokenAcquired: boolean;
          tokenClaims?: { tid?: string; aud?: string; iss?: string; appid?: string; oid?: string };
          foundryStatus?: number;
          foundryBody?: string;
          error?: string;
        }>;
        testConversationCreated: boolean;
        testConversationId?: string;
        testChat?: {
          ok: boolean;
          durationMs: number;
          status?: string;
          replyPreview?: string;
          toolCalls?: number;
          error?: string;
        };
        summary: string;
      }>('/ir-agent/health'),
  },

  // ─── Operations ───
  handoffs: {
    list: (days?: number, includeArchived?: boolean) => {
      const params = new URLSearchParams();
      if (days) params.set('days', String(days));
      if (includeArchived) params.set('includeArchived', 'true');
      const qs = params.toString();
      return get<Handoff[]>(`/handoffs${qs ? '?' + qs : ''}`);
    },
    listForAnalyst: (analystId: string, days?: number) => {
      const params = new URLSearchParams({ analystId });
      if (days) params.set('days', String(days));
      return get<Handoff[]>(`/handoffs?${params}`);
    },
    listForTargetAnalyst: (targetAnalystId: string, days?: number) => {
      const params = new URLSearchParams({ targetAnalystId });
      if (days) params.set('days', String(days));
      return get<Handoff[]>(`/handoffs?${params}`);
    },
    getById: (id: string) => get<Handoff>(`/handoffs/${id}`),
    create: (data: Partial<Handoff>) => post<Handoff>('/handoffs', data),
    update: (id: string, data: {
      incidents?: Array<{
        id: string;
        // any authed user:
        status?: string;
        notes?: string;
        acknowledged?: boolean;
        // author / admin only:
        title?: string;
        severity?: number;
        type?: string;
        customerName?: string;
        customerId?: string;
        freshServiceTicketId?: number;
        freshServiceTicketUrl?: string;
        targetAnalystId?: string;
        targetAnalystName?: string;
        targetAnalystEmail?: string;
      }>;
      notes?: string;
      archived?: boolean;
      incidentsClosed?: number;
      incidentsEscalated?: number;
      incidentsOpen?: number;
    }) => patch<Handoff>(`/handoffs/${id}`, data),
    delete: (id: string) => del<{ ok: boolean }>(`/handoffs/${id}`),
  },

  freshService: {
    tickets: (params?: { status?: string; priority?: string }) => {
      const qs = params ? new URLSearchParams(params as any).toString() : '';
      return get<any[]>(`/freshservice/tickets${qs ? '?' + qs : ''}`);
    },
    /** Open (2) + Pending (3) tickets for the handoff import picker. The
     *  endpoint responds { tickets, stats, message? } — NOT a bare array;
     *  `message` is set when the server has no API key configured. */
    openTickets: () =>
      get<{ tickets: any[]; stats?: any; message?: string }>('/freshservice/tickets?status=2,3'),
    /** Upload a file to a Freshservice ticket. Surfaces as a new note in the
     *  conversation thread. Reject mode: HTTP 413 if file > 25 MB. */
    uploadAttachment: (ticketId: number | string, file: File, opts?: { comment?: string; private?: boolean }) => {
      const fd = new FormData();
      fd.append('file', file);
      if (opts?.comment) fd.append('comment', opts.comment);
      if (opts?.private === false) fd.append('private', 'false');
      return uploadForm<{ conversation?: unknown } | unknown>(
        `/freshservice/tickets/${ticketId}/attachments`,
        fd,
      );
    },
  },

  notifications: {
    list: (unread?: boolean) =>
      get<Notification[]>(`/notifications${unread ? '?unread=true' : ''}`),
    markRead: (id: string) => patch<Notification>(`/notifications/${id}/read`, {}),
    markAllRead: () => post<{ marked: number }>('/notifications/read-all'),
  },

  activity: {
    list: (params?: { days?: number; type?: string; userId?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return get<ActivityEntry[]>(`/activity${qs ? '?' + qs : ''}`);
    },
  },

  customers: {
    list: (tier?: string) => {
      const qs = tier ? `?tier=${tier}` : '';
      return get<Customer[]>(`/customers${qs}`);
    },
    get: (id: string) => get<Customer>(`/customers/${id}`),
    create: (data: Partial<Customer>) => post<Customer>('/customers', data),
  },

  // ─── Customer Onboarding (Sentinel watchlist pipeline — Patriot-internal) ───
  onboarding: {
    preview: (input: Record<string, unknown>) =>
      post<OnboardingPreviewResponse>('/onboarding/preview', input),
    diagnose: () => get<OnboardingDiagnoseResponse>('/onboarding/diagnose'),
    listRuns: (status?: string) =>
      get<OnboardingRunDoc[]>(`/onboarding/runs${status ? `?status=${status}` : ''}`),
    getRun: (id: string) => get<OnboardingRunDoc>(`/onboarding/runs/${id}`),
    createRun: (body: { input: Record<string, unknown>; socScheduledFor?: string; socNotes?: string }) =>
      post<OnboardingRunDoc>('/onboarding/runs', body),
    approve: (id: string) => post<OnboardingRunDoc>(`/onboarding/runs/${id}/approve`),
    reject: (id: string, reason?: string) => post<OnboardingRunDoc>(`/onboarding/runs/${id}/reject`, { reason }),
    retry: (id: string) => post<OnboardingRunDoc>(`/onboarding/runs/${id}/retry`),
    cancel: (id: string) => post<OnboardingRunDoc>(`/onboarding/runs/${id}/cancel`),
    remove: (id: string) => del<{ ok: boolean }>(`/onboarding/runs/${id}`),
    scheduleAnnouncement: (id: string, scheduledFor: string | null, notes?: string) =>
      post<OnboardingRunDoc>(`/onboarding/runs/${id}/schedule-announcement`, { scheduledFor, notes }),
    sendAnnouncement: (id: string) => post<OnboardingRunDoc>(`/onboarding/runs/${id}/send-announcement`),
    // Re-write all 6 watchlist rows from the customer's stored fields (drift fix).
    repushWatchlists: (customerName: string) =>
      post<{ ok: boolean; watchlistSync?: unknown; skippedReason?: string; error?: string }>(
        `/customers/${encodeURIComponent(customerName)}/watchlist-repush`, {}),
  },

  // ─── Log Analytics ───
  logAnalytics: {
    slaMetrics: () => get<SLAMetricsResponse>('/log-analytics/sla-metrics'),
    analystWorkload: () => get<AnalystWorkloadResponse>('/log-analytics/analyst-workload'),
    incidentsSummary: () => get<IncidentsSummaryResponse>('/log-analytics/incidents-summary'),
    sync: () => post<{ ok: boolean; tenantCount: number; errorCount: number; queriedAt: string }>('/log-analytics/sync'),
  },

  // ─── Tenant Workspaces ───
  tenants: {
    list: () => get<TenantWorkspace[]>('/tenants'),
    create: (data: Omit<TenantWorkspace, 'id' | 'lastSyncAt' | 'lastError' | 'createdAt' | 'updatedAt'>) =>
      post<TenantWorkspace>('/tenants', data),
    update: (id: string, data: Partial<TenantWorkspace>) =>
      patch<TenantWorkspace>(`/tenants/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/tenants/${id}`),
  },

  // ─── Tickets ───
  tickets: {
    list: (params?: { status?: string; customerId?: string; assigneeId?: string; type?: string; limit?: number }) => {
      const qs = params ? new URLSearchParams(params as any).toString() : '';
      return get<Ticket[]>(`/tickets${qs ? '?' + qs : ''}`);
    },
    get: (id: string) => get<Ticket>(`/tickets/${id}`),
    create: (data: Partial<Ticket>) => post<Ticket>('/tickets', data),
    update: (id: string, data: Partial<Ticket>) => patch<Ticket>(`/tickets/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/tickets/${id}`),
    addComment: (id: string, content: string, isInternal: boolean) =>
      post<Ticket>(`/tickets/${id}/comments`, { content, isInternal }),
    setStatus: (id: string, status: string) =>
      post<Ticket>(`/tickets/${id}/status`, { status }),
  },

  // ─── Escalations ───
  escalations: {
    list: (params?: { status?: string; days?: number; limit?: number; offset?: number }) => {
      const qs = params ? new URLSearchParams(params as any).toString() : '';
      return get<IncidentEscalation[]>(`/escalations${qs ? '?' + qs : ''}`);
    },
    get: (id: string) => get<IncidentEscalation>(`/escalations/${id}`),
    create: (data: Partial<IncidentEscalation>) => post<IncidentEscalation>('/escalations', data),
    update: (id: string, data: Partial<IncidentEscalation>) =>
      patch<IncidentEscalation>(`/escalations/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/escalations/${id}`),
    acknowledge: (id: string) => post<IncidentEscalation>(`/escalations/${id}/acknowledge`),
    resolve: (id: string) => post<IncidentEscalation>(`/escalations/${id}/resolve`),
    bumpSeverity: (id: string) => post<IncidentEscalation>(`/escalations/${id}/bump-severity`),
  },

  // ─── Incident Response Cases ───
  incidentResponses: {
    list: (params?: { status?: string; days?: number }) => {
      const qs = params ? new URLSearchParams(params as any).toString() : '';
      return get<IncidentResponseCase[]>(`/incident-responses${qs ? '?' + qs : ''}`);
    },
    get: (id: string) => get<IncidentResponseCase>(`/incident-responses/${id}`),
    create: (data: Partial<IncidentResponseCase>) => post<IncidentResponseCase>('/incident-responses', data),
    update: (id: string, data: Partial<IncidentResponseCase>) =>
      patch<IncidentResponseCase>(`/incident-responses/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/incident-responses/${id}`),
    addNote: (id: string, content: string, agentTurn?: import('../types').IRAgentTurn) =>
      post<IncidentResponseCase>(`/incident-responses/${id}/notes`, agentTurn ? { content, agentTurn } : { content }),
    addIoc: (id: string, ioc: { type: string; value: string; description?: string; observedAt?: string }) =>
      post<IncidentResponseCase>(`/incident-responses/${id}/iocs`, ioc),
    removeIoc: (id: string, iocId: string) =>
      del<IncidentResponseCase>(`/incident-responses/${id}/iocs/${iocId}`),
    addTimelineEvent: (id: string, event: { type: string; description: string; occurredAt?: string }) =>
      post<IncidentResponseCase>(`/incident-responses/${id}/timeline`, event),
    close: (id: string) => post<IncidentResponseCase>(`/incident-responses/${id}/close`),
  },

  // ─── Roster Slots ───
  rosterSlots: {
    list: (date?: string) => {
      const qs = date ? `?date=${date}` : '';
      return get<RosterSlot[]>(`/roster${qs}`);
    },
    create: (data: Partial<RosterSlot>) => post<RosterSlot>('/roster', data),
    updateSlot: (id: string, data: Partial<RosterSlot>) =>
      patch<RosterSlot>(`/roster/${id}`, data),
    toggleInactive: (id: string) => post<RosterSlot>(`/roster/${id}/toggle-inactive`),
    remove: (id: string) => del<{ ok: boolean }>(`/roster/${id}`),
  },

  // ─── Roster Management Module (full drag-and-drop board + profile editor) ───
  rosterModule: {
    get: () => get<{ zones: Record<string, unknown> | null; updatedAt: string | null; updatedBy: { userId: string; name: string; email: string } | null }>('/roster-module'),
    /** Optimistic full-blob save: pass the updatedAt you loaded as
     *  baseUpdatedAt — a 409 means another editor saved first and the caller
     *  must reload before saving again. Omitting baseUpdatedAt keeps the
     *  legacy last-writer-wins behavior (scripts/back-compat only). */
    save: (zones: Record<string, unknown>, baseUpdatedAt?: string | null) =>
      put<{ zones: Record<string, unknown>; updatedAt: string; updatedBy: { userId: string; name: string; email: string } }>(
        '/roster-module', { zones, ...(baseUpdatedAt ? { baseUpdatedAt } : {}) }),
    // Person-level operations — etag-guarded server-side so two editors
    // touching different people can't clobber each other via whole-doc saves.
    /** 409s when a card with the same email/name exists (unless allowDuplicate);
     *  idempotent by hiredFromCandidateId (returns the existing card with
     *  alreadyOnRoster: true — used by the Hiring "Add to roster" flow). */
    addPerson: (data: {
      zoneId: string;
      person?: Partial<import('../lib/rosterData').RosterPerson>;
      allowDuplicate?: boolean;
      hiredFromCandidateId?: string;
    }) =>
      post<{ person: import('../lib/rosterData').RosterPerson; zoneId: string; alreadyOnRoster: boolean }>(
        '/roster-module/people', data),
    /** Field merge on one card; SYNC_FIELDS propagate to linked cards server-side. */
    updatePerson: (personId: string, data: Partial<import('../lib/rosterData').RosterPerson>) =>
      patch<{ person: import('../lib/rosterData').RosterPerson; zoneId: string }>(
        `/roster-module/people/${personId}`, data),
    movePerson: (personId: string, toZoneId: string) =>
      post<{ person: import('../lib/rosterData').RosterPerson; zoneId: string }>(
        `/roster-module/people/${personId}/move`, { toZoneId }),
    /** Destructive — requires the roster-mgmt delete permission. */
    removePerson: (personId: string) => del<{ ok: boolean }>(`/roster-module/people/${personId}`),
    /** One-click offboarding (admin-only): strips every roster card for the
     *  person across all zones AND deletes their hub account (by email,
     *  unless removeAccount:false). History (QA, handoffs, acks) is kept. */
    offboardAnalyst: (data: { name?: string; analystId?: string; email?: string; removeAccount?: boolean }) =>
      post<{ person: string; rosterCardsRemoved: number; zonesCleared: string[]; accountRemoved: boolean; accountId: string | null }>(
        '/hub-admin/offboard-analyst', { ...data, confirm: true }),
  },

  // ─── Shift Coverage (docs/shift-coverage-design.md) ───
  // Coverage is derived from the assignment sheet + roster; acks are per
  // (client, date, shift) records in shift-coverage-acks. All staff-gated.
  shiftCoverage: {
    /** Full heat map for one shift instance (the IC Coverage view). */
    get: (date: string, shift: string) =>
      get<import('../types').ShiftCoverageResponse>(
        `/shift-coverage?date=${encodeURIComponent(date)}&shift=${encodeURIComponent(shift)}`),
    /** One analyst's assigned clients + ack state (the handoff gate).
     *  `who` picks whose checklist — pass it ONLY while impersonating, so the
     *  impersonated analyst resolves instead of the admin the request
     *  authenticates as. Either field present → the identity is used verbatim
     *  as a whole; omitted/empty → the server resolves the caller's claims
     *  (which is what the server-side handoff gate resolves — keep symmetry). */
    mine: (date: string, shift: string, who?: { email?: string; name?: string }) => {
      let qs = `date=${encodeURIComponent(date)}&shift=${encodeURIComponent(shift)}`;
      if (who?.email) qs += `&email=${encodeURIComponent(who.email)}`;
      if (who?.name) qs += `&name=${encodeURIComponent(who.name)}`;
      return get<import('../types').MyCoverageResponse>(`/shift-coverage/mine?${qs}`);
    },
    /** Upsert one ack/flag; reason is required when status is 'flagged',
     *  optional for acks (an IC acking on an analyst's behalf notes why). */
    ack: (data: { client: string; date: string; shift: string; status: 'acked' | 'flagged'; reason?: string }) =>
      post<import('../types').CoverageAck & { client: string; date: string; shift: string }>('/shift-coverage/ack', data),
  },

  // ─── Shift Attendance (the people-side mirror of coverage acks) ───
  shiftAttendance: {
    /** The attendance board for one shift instance (IC Coverage view). */
    get: (date: string, shift: string) =>
      get<import('../types').ShiftAttendanceResponse>(
        `/shift-attendance?date=${encodeURIComponent(date)}&shift=${encodeURIComponent(shift)}`),
    /** Mark one rostered person present/absent; 'expected' clears the mark.
     *  coveredBy records who fills an absent person's position. */
    mark: (data: { person: string; date: string; shift: string; status: 'present' | 'absent' | 'expected'; reason?: string; coveredBy?: string }) =>
      post<any>('/shift-attendance/mark', data),
  },

  // ─── Roster (alias with richer params) ───
  roster: {
    list: (params?: { shift?: string; date?: string }) => {
      const qs = params ? new URLSearchParams(params as any).toString() : '';
      return get<RosterSlot[]>(`/roster${qs ? '?' + qs : ''}`);
    },
    create: (data: Partial<RosterSlot>) => post<RosterSlot>('/roster', data),
    update: (id: string, data: Partial<RosterSlot>) => patch<RosterSlot>(`/roster/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/roster/${id}`),
  },

  // ─── Retention ───
  retention: {
    list: () => get<{ policies: any[] }>('/retention/policies'),
    update: (id: string, data: any) =>
      request<{ id: string; updated: boolean }>(`/retention/policies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  },

  // ─── QA ───
  // Analyst view: `scores()` / `reports()` are server-side scoped to the caller
  // for non-admins. Admins / `qa_admin` editors see all rows; everything else
  // is admin-gated server-side.
  qa: {
    scores: (params?: { analystId?: string; periodStart?: string; scope?: 'self' | 'team' }) => {
      const qs = qaQuery(params);
      return get<import('../types').QAScoreRecord[]>(`/qa-scores${qs ? '?' + qs : ''}`);
    },
    getScore: (id: string) => get<import('../types').QAScoreRecord>(`/qa-scores/${id}`),
    createScore: (data: Partial<import('../types').QAScoreRecord>) =>
      post<import('../types').QAScoreRecord>('/qa-scores', data),
    updateScore: (id: string, data: Partial<import('../types').QAScoreRecord>) =>
      patch<import('../types').QAScoreRecord>(`/qa-scores/${id}`, data),
    deleteScore: (id: string) => del<{ ok: boolean }>(`/qa-scores/${id}`),
    importScores: (records: Partial<import('../types').QAScoreRecord>[]) =>
      post<{ created: number; updated: number }>('/qa-scores/import', { records }),
    reports: (params?: { analystId?: string; periodStart?: string; scope?: 'self' | 'team' }) => {
      const qs = qaQuery(params);
      return get<import('../types').QAReportRecord[]>(`/qa-reports${qs ? '?' + qs : ''}`);
    },
    getReport: (id: string) => get<import('../types').QAReportRecord>(`/qa-reports/${id}`),
    uploadReport: (file: File, meta: { analystId: string | null; analystDisplayName: string; analystNumber?: number | null; periodStart: string; title?: string; linkScoreId?: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('analystDisplayName', meta.analystDisplayName);
      if (meta.analystId) fd.append('analystId', meta.analystId);
      if (meta.analystNumber != null) fd.append('analystNumber', String(meta.analystNumber));
      fd.append('periodStart', meta.periodStart);
      if (meta.title) fd.append('title', meta.title);
      if (meta.linkScoreId) fd.append('linkScoreId', meta.linkScoreId);
      return uploadForm<import('../types').QAReportRecord>('/qa-reports', fd);
    },
    deleteReport: (id: string) => del<{ ok: boolean }>(`/qa-reports/${id}`),
  },

  // ─── RBAC ───
  rbac: {
    listRoles: () => get<CustomRole[]>('/rbac/roles'),
    listAssignments: () => get<UserRoleAssignment[]>('/rbac/assignments'),
    createRole: (data: Partial<CustomRole>) => post<CustomRole>('/rbac/roles', data),
    updateRole: (id: string, data: Partial<CustomRole>) =>
      request<CustomRole>(`/rbac/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteRole: (id: string) => del<{ ok: boolean }>(`/rbac/roles/${id}`),
    assign: (userId: string, customRoleId: string) =>
      post<{ ok: boolean }>('/rbac/assignments', { userId, customRoleId }),
    unassign: (userId: string, roleId: string) =>
      del<{ ok: boolean }>(`/rbac/assignments/${userId}/${roleId}`),
  },

  // ─── Checklist ───
  checklist: {
    items: (shift?: string) => {
      const qs = shift ? `?shift=${shift}` : '';
      return get<ApiChecklistItem[]>(`/checklist-items${qs}`);
    },
    createItem: (data: Partial<ApiChecklistItem>) => post<ApiChecklistItem>('/checklist-items', data),
    updateItem: (id: string, data: Partial<ApiChecklistItem>) => patch<ApiChecklistItem>(`/checklist-items/${id}`, data),
    deleteItem: (id: string) => del<{ ok: boolean }>(`/checklist-items/${id}`),
    seedDefaults: (items: Partial<ApiChecklistItem>[]) => post<ApiChecklistItem[]>('/checklist-items/seed', { items }),
    completions: (date?: string) => {
      const qs = date ? `?date=${date}` : '';
      return get<ApiChecklistCompletion[]>(`/checklist-completions${qs}`);
    },
    complete: (data: { itemId: string; shift: string; date?: string }) =>
      post<ApiChecklistCompletion>('/checklist-completions', data),
  },

  // ─── Sign-In Audit + Presence ───
  signin: {
    /** Record a manual sign-in event (the GET /api/me path also auto-logs). */
    event: () => post<{ ok: boolean }>('/signin/event'),
    /** List recent sign-in events. days: 1-90, limit: 1-500. */
    log: (params?: { days?: number; userId?: string; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.days) qs.set('days', String(params.days));
      if (params?.userId) qs.set('userId', params.userId);
      if (params?.limit) qs.set('limit', String(params.limit));
      const s = qs.toString();
      return get<{ events: import('../types').SignInEvent[]; days: number; queriedAt: string }>(
        `/signin/log${s ? '?' + s : ''}`,
      );
    },
  },
  presence: {
    heartbeat: (view: string) =>
      post<{ ok: boolean; lastSeenAt: string }>('/presence/heartbeat', { view }),
    online: (windowSec?: number) => {
      const qs = windowSec ? `?windowSec=${windowSec}` : '';
      return get<{
        users: Array<{
          userId: string;
          userName: string;
          email: string;
          view: string;
          lastSeenAt: string;
          lastSeenMs: number;
        }>;
        queriedAt: string;
        windowSec: number;
      }>(`/presence/online${qs}`);
    },
  },

  // ─── Bulletin Board ───
  bulletin: {
    list: (limit?: number) => {
      const qs = limit ? `?limit=${limit}` : '';
      return get<{ posts: import('../types').BulletinPost[]; retentionDays: number }>(`/bulletin/posts${qs}`);
    },
    create: (data: Partial<import('../types').BulletinPost>) =>
      post<import('../types').BulletinPost>('/bulletin/posts', data),
    update: (id: string, data: Partial<import('../types').BulletinPost>) =>
      patch<import('../types').BulletinPost>(`/bulletin/posts/${id}`, data),
    remove: (id: string, reason?: string) => {
      const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
      return del<{ ok: boolean }>(`/bulletin/posts/${id}${qs}`);
    },
    pin: (id: string, pinned: boolean) =>
      post<import('../types').BulletinPost>(`/bulletin/posts/${id}/pin`, { pinned }),
    react: (id: string, emoji: string, commentId?: string) =>
      post<import('../types').BulletinPost>(`/bulletin/posts/${id}/react`, { emoji, commentId }),
    addComment: (id: string, data: { body: string; mediaUrl?: string; linkUrl?: string }) =>
      post<import('../types').BulletinPost>(`/bulletin/posts/${id}/comments`, data),
    updateComment: (postId: string, commentId: string, data: { body?: string; mediaUrl?: string; linkUrl?: string }) =>
      patch<import('../types').BulletinPost>(`/bulletin/posts/${postId}/comments/${commentId}`, data),
    deleteComment: (postId: string, commentId: string) =>
      del<{ ok: boolean }>(`/bulletin/posts/${postId}/comments/${commentId}`),
    retention: () => get<{ retentionDays: number }>('/bulletin/retention'),
    runRetention: () => post<{ ranAt: string; retentionDays: number; scrubbedPosts: number; scrubbedComments: number; checked: number }>('/bulletin/retention/run'),
    /** Upload an image/GIF/mp4 to the bulletin-media blob container. */
    uploadMedia: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return uploadForm<{ ok: true; url: string; contentType: string; size: number; filename: string }>(
        '/bulletin/upload',
        fd,
      );
    },
  },

  // ─── Decision Trees ───
  decisionTrees: {
    list: () => get<DecisionTreeRecord[]>('/decision-trees'),
    get: (id: string) => get<DecisionTreeRecord>(`/decision-trees/${id}`),
    create: (data: Partial<DecisionTreeRecord>) => post<DecisionTreeRecord>('/decision-trees', data),
    update: (id: string, data: Partial<DecisionTreeRecord>) => patch<DecisionTreeRecord>(`/decision-trees/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/decision-trees/${id}`),
    generate: (description: string) =>
      post<DecisionTreeRecord>('/decision-trees/generate', { description }),
  },

  // ─── KQL Library ───
  kql: {
    list: () => get<import('../types').KqlQuerySummary[]>('/kql-queries'),
    get: (id: string) => get<import('../types').KqlQuery>(`/kql-queries/${id}`),
    create: (data: Partial<import('../types').KqlQuery>) => post<import('../types').KqlQuery>('/kql-queries', data),
    update: (id: string, data: Partial<import('../types').KqlQuery>) => patch<import('../types').KqlQuery>(`/kql-queries/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/kql-queries/${id}`),
    sources: {
      list: () => get<{ sources: import('../types').KqlSource[]; recommended: import('../types').KqlRecommendedSource[] }>('/kql-sources'),
      create: (data: Partial<import('../types').KqlSource>) => post<import('../types').KqlSource>('/kql-sources', data),
      update: (id: string, data: Partial<import('../types').KqlSource>) => patch<import('../types').KqlSource>(`/kql-sources/${id}`, data),
      remove: (id: string) => del<{ ok: boolean; removedQueries: number }>(`/kql-sources/${id}`),
      sync: (id: string) => post<{ source: import('../types').KqlSource; stats?: { scanned: number; added: number; updated: number; removed: number; skipped: number; truncated: boolean }; error?: string }>(`/kql-sources/${id}/sync`),
    },
  },

  // ─── API Keys ───
  apiKeys: {
    list: () => get<import('../types').ApiKeySummary[]>('/api-keys'),
    get: (id: string) => get<import('../types').ApiKeySummary>(`/api-keys/${id}`),
    create: (data: {
      name: string;
      bindingType: 'system' | 'user';
      bindingUserId?: string;
      accessMode?: import('../types').ApiKeyAccessMode;
      permissions: import('../types').ApiKeyPermissions;
      expiresAt?: string | null;
      description?: string;
    }) => post<{ key: import('../types').ApiKeySummary; secret: string }>('/api-keys', data),
    update: (id: string, data: Partial<{
      name: string;
      description: string;
      expiresAt: string | null;
      accessMode: import('../types').ApiKeyAccessMode;
      permissions: import('../types').ApiKeyPermissions;
    }>) => patch<import('../types').ApiKeySummary>(`/api-keys/${id}`, data),
    revoke: (id: string) => post<import('../types').ApiKeySummary>(`/api-keys/${id}/revoke`),
    rotate: (id: string) =>
      post<{ key: import('../types').ApiKeySummary; secret: string }>(`/api-keys/${id}/rotate`),
  },

  // ─── Engineering Requests (Hub-native analyst flow) ───
  engineeringRequests: {
    /** Submit a new analyst engineering request. Returns the new id + the
     *  created Freshservice ticket id (may be null if FS isn't configured). */
    analystSubmit: (body: {
      title: string;
      category: 'detection' | 'automation' | 'tuning' | 'other';
      priority: 'low' | 'normal' | 'high' | 'urgent';
      description: string;
      desiredBy?: string | null;
      acknowledgement: true;
      submissionContext?: {
        referringRoute?: string;
        relatedIncidentId?: string;
        relatedDetectionId?: string;
      };
    }) =>
      post<{ requestId: string; freshserviceTicketId: number | null; freshserviceError?: string | null }>(
        '/engineering-requests/analyst-submit',
        body,
      ),
    /** List the caller's own analyst-submitted requests. */
    mine: () =>
      get<{ requests: import('../types').EngineeringRequest[] }>('/engineering-requests/mine'),
    /** Read a single own request. */
    get: (id: string) =>
      get<import('../types').EngineeringRequest>(`/engineering-requests/by-id/${id}`),
    /** Cancel a request — removes the Hub companion record and closes the
     *  linked Freshservice ticket (best-effort). */
    delete: (id: string) =>
      del<{ deleted: true; freshserviceTicketId: number | null; freshserviceCloseError: string | null }>(
        `/engineering-requests/by-id/${id}`,
      ),
  },

  // ─── Engineering SOC 2 Evidence ───
  engineeringEvidence: {
    /** File a SOC 2 evidence record for a resolved FS ticket. The server
     *  stamps `who` from the authenticated user and `when` from server clock —
     *  do NOT trust those fields from the caller. */
    create: (body: {
      ticketId: number;
      what: import('../types').EngineeringSOC2EvidenceChangeKind;
      changeDescription: string;
      approvals?: Array<{ approverId?: string; approverName: string; approvedAt?: string; comment?: string }>;
      attestations: import('../types').EngineeringSOC2EvidenceAttestations;
      linkedArtifacts?: import('../types').EngineeringSOC2EvidenceLinkedArtifacts;
    }) => post<import('../types').EngineeringSOC2Evidence>('/engineering/evidence', body),
    /** Fetch the evidence trail for a Freshservice ticket. `latest` is the
     *  most recent record; `history` is every record in reverse-chronological
     *  order for ticket-detail audit display. */
    getByTicket: (ticketId: number | string) =>
      get<{ latest: import('../types').EngineeringSOC2Evidence | null; history: import('../types').EngineeringSOC2Evidence[] }>(
        `/engineering/evidence/${ticketId}`,
      ),
  },

  // ─── Feature Requests ───
  featureRequests: {
    list: () => get<{ items: import('../types').FeatureRequestSummary[] }>('/feature-requests'),
    mine: () => get<{ items: import('../types').FeatureRequestSummary[] }>('/feature-requests/mine'),
    get: (id: string) => get<import('../types').FeatureRequest>(`/feature-requests/${id}`),
    update: (id: string, data: { status?: import('../types').FeatureRequestStatus; reviewerNote?: string; closedAt?: string }) =>
      patch<import('../types').FeatureRequest>(`/feature-requests/${id}`, data),
  },

  // ─── Permission (Access) Requests — Tier 3 approval workflow ───
  permissionRequests: {
    /** Submit a new access request (RBAC: permission_requests canEdit). */
    submit: (body: {
      viewId: string;
      access: import('../types').PermissionAccessLevel;
      justification: string;
      durationDays?: number | null;
    }) => post<import('../types').PermissionRequest>('/permission-requests', body),
    /** The caller's own requests. */
    mine: () => get<{ items: import('../types').PermissionRequest[] }>('/permission-requests/mine'),
    /** Full queue across all users (admin only). */
    list: () => get<{ items: import('../types').PermissionRequest[] }>('/permission-requests'),
    /** Approve or deny a pending request (admin only). `apply` defaults to
     *  true — approval writes the grant into rbac-roles/rbac-assignments. */
    decide: (id: string, body: { action: 'approve' | 'deny'; note?: string; apply?: boolean }) =>
      post<import('../types').PermissionRequest>(`/permission-requests/${id}/decision`, body),
    /** Withdraw an own pending request (kept as 'cancelled' for the audit trail). */
    cancel: (id: string) =>
      post<import('../types').PermissionRequest>(`/permission-requests/${id}/cancel`),
  },

  // ─── Provisioning Requests — cross-tenant infra approval workflow ───
  provisioning: {
    /** Submit a provisioning request (RBAC: engineering canEdit). */
    submit: (body: {
      type: import('../types').ProvisioningRequestType;
      target?: import('../types').ProvisioningTarget;
      spec: import('../types').ProvisioningSpec;
      businessJustification: string;
    }) => post<import('../types').ProvisioningRequest>('/provisioning-requests', body),
    /** The caller's own requests. */
    mine: () => get<{ items: import('../types').ProvisioningRequest[] }>('/provisioning-requests/mine'),
    /** Full queue (admin only). */
    list: () => get<{ items: import('../types').ProvisioningRequest[] }>('/provisioning-requests'),
    /** Approve (runs provisioning) or deny a pending request (admin only). */
    decide: (id: string, body: { action: 'approve' | 'deny'; note?: string }) =>
      post<import('../types').ProvisioningRequest>(`/provisioning-requests/${id}/decision`, body),
    /** Retry a failed request (admin only) — completed steps are skipped. */
    retry: (id: string) => post<import('../types').ProvisioningRequest>(`/provisioning-requests/${id}/retry`, {}),
    /** Withdraw an own pending request. */
    cancel: (id: string) => post<import('../types').ProvisioningRequest>(`/provisioning-requests/${id}/cancel`, {}),
    /** Live tenant-2 catalogs for the request form. */
    catalog: (
      kind: 'app-registrations' | 'managed-identities' | 'key-vaults' | 'graph-permissions'
        | 'resource-groups' | 'azure-roles' | 'principals',
      refresh?: boolean,
    ) => get<{ items: any[] }>(`/provisioning/catalog/${kind}${refresh ? '?refresh=1' : ''}`),
    /** Supply (or rotate) an approved Key Vault secret's value. The value is
     *  forwarded straight to Key Vault — never stored in the Hub, never logged,
     *  never echoed back. Only the request metadata is returned. */
    setSecretValue: (id: string, value: string) =>
      post<import('../types').ProvisioningRequest>(`/provisioning-requests/${id}/secret-value`, { value }),
  },

  // ─── IOC Watchlist ───
  iocWatchlist: {
    /** Available week keys for a KB + the current ISO week. */
    listWeeks: (kbId: string) =>
      get<{
        currentWeek: string;
        weeks: Array<{ id: string; weekKey: string | null; title: string; updatedAt: string; createdAt: string; isLegacy: boolean }>;
      }>(`/kb/${encodeURIComponent(kbId)}/ioc-watchlist/weeks`),

    /**
     * Download the Microsoft Threat Intelligence CSV export for one week.
     * Bearer-aware blob fetch — necessary because the standard `request()`
     * helper assumes JSON and would mangle the CSV payload.
     */
    downloadCsv: async (kbId: string, weekKey: string): Promise<Blob> => {
      let authHeader: Record<string, string> = {};
      try {
        const { getAccessToken } = await import('../lib/auth/msalConfig');
        const token = await getAccessToken();
        if (token) authHeader = { Authorization: `Bearer ${token}` };
      } catch {
        // No token available — SWA proxy will still inject x-ms-client-principal.
      }
      const url = `${BASE_URL}/kb/${encodeURIComponent(kbId)}/ioc-watchlist/${encodeURIComponent(weekKey)}/export.csv`;
      const resp = await fetch(url, { headers: { ...authHeader } });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`MS TI export failed (${resp.status}): ${text || resp.statusText}`);
      }
      return await resp.blob();
    },

    /**
     * Add a hand-curated IOC to a week's watchlist. The entry is flagged
     * manual (survives Rebuild backfills) and flows into the MS TI CSV export.
     * Returns the full updated watchlist article for in-place refresh.
     */
    addEntry: (kbId: string, weekKey: string, data: { type: string; value: string; note?: string }) =>
      post<{ article: import('../types').Article }>(
        `/kb/${encodeURIComponent(kbId)}/ioc-watchlist/${encodeURIComponent(weekKey)}/entries`,
        data,
      ),

    /**
     * Remove one entry (extracted or manual) from a week's watchlist. The
     * server records a suppression tombstone so the extraction pipelines don't
     * re-add the same type+value on their next run.
     */
    removeEntry: (kbId: string, weekKey: string, entryId: string) =>
      del<{ article: import('../types').Article }>(
        `/kb/${encodeURIComponent(kbId)}/ioc-watchlist/${encodeURIComponent(weekKey)}/entries/${encodeURIComponent(entryId)}`,
      ),
  },

  // ─── GM — EOS/Traction (Phase 2 of the RMS→Hub integration) ───
  // Reads require staff; writes require RBAC edit on the 'traction' view
  // (deletes require the delete permission). Backed by api/functions/gm-traction.ts.
  gmTraction: {
    rocks: {
      list: () => get<import('../types').GmRock[]>('/gm/traction/rocks'),
      create: (data: Partial<import('../types').GmRock>) =>
        post<import('../types').GmRock>('/gm/traction/rocks', data),
      update: (id: string, data: Partial<import('../types').GmRock>) =>
        patch<import('../types').GmRock>(`/gm/traction/rocks/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/traction/rocks/${id}`),
    },
    scorecard: {
      list: () => get<import('../types').GmScorecardMetric[]>('/gm/traction/scorecard'),
      create: (data: Partial<import('../types').GmScorecardMetric>) =>
        post<import('../types').GmScorecardMetric>('/gm/traction/scorecard', data),
      update: (id: string, data: Partial<import('../types').GmScorecardMetric>) =>
        patch<import('../types').GmScorecardMetric>(`/gm/traction/scorecard/${id}`, data),
      /** Merge a single weekly value server-side. value null clears the cell. */
      setWeek: (id: string, weekKey: string, value: number | null) =>
        post<import('../types').GmScorecardMetric>(`/gm/traction/scorecard/${id}/week`, { weekKey, value }),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/traction/scorecard/${id}`),
    },
    issues: {
      list: () => get<import('../types').GmIssue[]>('/gm/traction/issues'),
      create: (data: Partial<import('../types').GmIssue>) =>
        post<import('../types').GmIssue>('/gm/traction/issues', data),
      update: (id: string, data: Partial<import('../types').GmIssue>) =>
        patch<import('../types').GmIssue>(`/gm/traction/issues/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/traction/issues/${id}`),
    },
    todos: {
      list: () => get<import('../types').GmTodo[]>('/gm/traction/todos'),
      create: (data: Partial<import('../types').GmTodo>) =>
        post<import('../types').GmTodo>('/gm/traction/todos', data),
      update: (id: string, data: Partial<import('../types').GmTodo>) =>
        patch<import('../types').GmTodo>(`/gm/traction/todos/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/traction/todos/${id}`),
    },
    meetings: {
      list: () => get<import('../types').GmL10Meeting[]>('/gm/traction/l10'),
      /** Persist a concluded L10 meeting. clearDoneTodos also deletes completed
       *  to-dos server-side so only open ones carry over; the cleared ids come
       *  back so the client can update its cache without a refetch. */
      create: (data: Partial<import('../types').GmL10Meeting> & { clearDoneTodos?: boolean }) =>
        post<{ meeting: import('../types').GmL10Meeting; clearedTodoIds: string[] }>('/gm/traction/l10', data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/traction/l10/${id}`),
    },
    people: {
      list: () => get<import('../types').GmPersonAnalysis[]>('/gm/traction/people'),
      create: (data: Partial<import('../types').GmPersonAnalysis>) =>
        post<import('../types').GmPersonAnalysis>('/gm/traction/people', data),
      update: (id: string, data: Partial<import('../types').GmPersonAnalysis>) =>
        patch<import('../types').GmPersonAnalysis>(`/gm/traction/people/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/traction/people/${id}`),
      getConfig: () => get<{ coreValues: string[] }>('/gm/traction/people-config'),
      saveConfig: (coreValues: string[]) =>
        put<{ coreValues: string[] }>('/gm/traction/people-config', { coreValues }),
    },
  },

  // ─── GM — Hiring pipeline (Phase 3 of the RMS→Hub integration) ───
  // Reads require staff; writes require RBAC edit on the 'hiring' view
  // (deletes require the delete permission). Backed by api/functions/gm-hiring.ts.
  gmHiring: {
    requisitions: {
      list: () => get<import('../types').GmRequisition[]>('/gm/hiring/requisitions'),
      create: (data: Partial<import('../types').GmRequisition>) =>
        post<import('../types').GmRequisition>('/gm/hiring/requisitions', data),
      update: (id: string, data: Partial<import('../types').GmRequisition>) =>
        patch<import('../types').GmRequisition>(`/gm/hiring/requisitions/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/hiring/requisitions/${id}`),
    },
    candidates: {
      list: () => get<import('../types').GmCandidate[]>('/gm/hiring/candidates'),
      create: (data: Partial<import('../types').GmCandidate>) =>
        post<import('../types').GmCandidate>('/gm/hiring/candidates', data),
      /** Profile fields only — stage moves go through moveStage so the
       *  server appends the stage-history entry atomically. */
      update: (id: string, data: Partial<import('../types').GmCandidate>) =>
        patch<import('../types').GmCandidate>(`/gm/hiring/candidates/${id}`, data),
      moveStage: (id: string, stage: import('../types').GmCandidateStage) =>
        post<import('../types').GmCandidate>(`/gm/hiring/candidates/${id}/stage`, { stage }),
      /** Also cascade-deletes the candidate's interviews server-side; the
       *  removed ids come back so the client can update its cache. */
      remove: (id: string) =>
        del<{ ok: boolean; removedInterviewIds: string[] }>(`/gm/hiring/candidates/${id}`),
    },
    interviews: {
      list: () => get<import('../types').GmInterview[]>('/gm/hiring/interviews'),
      create: (data: Partial<import('../types').GmInterview>) =>
        post<import('../types').GmInterview>('/gm/hiring/interviews', data),
      update: (id: string, data: Partial<import('../types').GmInterview>) =>
        patch<import('../types').GmInterview>(`/gm/hiring/interviews/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/hiring/interviews/${id}`),
    },
  },

  // ─── GM — People-Ops (Phase 4 of the RMS→Hub integration) ───
  // Reads require staff; writes require RBAC edit on the 'people' view
  // (deletes require the delete permission). Backed by api/functions/gm-people.ts.
  gmPeople: {
    reviews: {
      list: () => get<import('../types').GmReview[]>('/gm/people/reviews'),
      create: (data: Partial<import('../types').GmReview>) =>
        post<import('../types').GmReview>('/gm/people/reviews', data),
      update: (id: string, data: Partial<import('../types').GmReview>) =>
        patch<import('../types').GmReview>(`/gm/people/reviews/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/people/reviews/${id}`),
    },
    oneOnOnes: {
      list: () => get<import('../types').GmOneOnOne[]>('/gm/people/oneonones'),
      create: (data: Partial<import('../types').GmOneOnOne>) =>
        post<import('../types').GmOneOnOne>('/gm/people/oneonones', data),
      /** Note fields only — action items go through the item endpoints below
       *  so the server merges them etag-guarded against the current document. */
      update: (id: string, data: Partial<import('../types').GmOneOnOne>) =>
        patch<import('../types').GmOneOnOne>(`/gm/people/oneonones/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/people/oneonones/${id}`),
      addItem: (id: string, text: string) =>
        post<import('../types').GmOneOnOne>(`/gm/people/oneonones/${id}/items`, { text }),
      updateItem: (id: string, itemId: string, data: { text?: string; done?: boolean }) =>
        patch<import('../types').GmOneOnOne>(`/gm/people/oneonones/${id}/items/${itemId}`, data),
      /** Edit-gated (note-content change, not record deletion); returns the updated note. */
      removeItem: (id: string, itemId: string) =>
        del<import('../types').GmOneOnOne>(`/gm/people/oneonones/${id}/items/${itemId}`),
    },
    wellbeing: {
      list: () => get<import('../types').GmWellbeingEntry[]>('/gm/people/wellbeing'),
      create: (data: Partial<import('../types').GmWellbeingEntry>) =>
        post<import('../types').GmWellbeingEntry>('/gm/people/wellbeing', data),
      update: (id: string, data: Partial<import('../types').GmWellbeingEntry>) =>
        patch<import('../types').GmWellbeingEntry>(`/gm/people/wellbeing/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/people/wellbeing/${id}`),
    },
    attrition: {
      list: () => get<import('../types').GmAttritionRecord[]>('/gm/people/attrition'),
      /** 409s if the team member already has a record — update that instead. */
      create: (data: Partial<import('../types').GmAttritionRecord>) =>
        post<import('../types').GmAttritionRecord>('/gm/people/attrition', data),
      update: (id: string, data: Partial<import('../types').GmAttritionRecord>) =>
        patch<import('../types').GmAttritionRecord>(`/gm/people/attrition/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/people/attrition/${id}`),
    },
  },

  // ─── GM — Discipline (Phase 5 of the RMS→Hub integration) ───
  // Reads require staff; writes require RBAC edit on the 'discipline' view
  // (deletes require the delete permission). Backed by api/functions/gm-discipline.ts.
  gmDiscipline: {
    records: {
      list: () => get<import('../types').GmDisciplinaryRecord[]>('/gm/discipline/records'),
      create: (data: Partial<import('../types').GmDisciplinaryRecord>) =>
        post<import('../types').GmDisciplinaryRecord>('/gm/discipline/records', data),
      /** Detail fields only — status moves and acknowledgements go through the
       *  dedicated endpoints below so the server stamps who/when atomically. */
      update: (id: string, data: Partial<import('../types').GmDisciplinaryRecord>) =>
        patch<import('../types').GmDisciplinaryRecord>(`/gm/discipline/records/${id}`, data),
      /** Move between active/expired/rescinded; clearing a strike records who
       *  resolved it and when, reactivating clears that attribution. */
      setStatus: (id: string, status: import('../types').GmDisciplineStatus) =>
        post<import('../types').GmDisciplinaryRecord>(`/gm/discipline/records/${id}/status`, { status }),
      /** Record whether the team member acknowledged the warning. */
      setAcknowledged: (id: string, acknowledged: boolean) =>
        post<import('../types').GmDisciplinaryRecord>(`/gm/discipline/records/${id}/acknowledge`, { acknowledged }),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/discipline/records/${id}`),
    },
  },

  // ─── GM — Executive Reports (Phase 6 of the RMS→Hub integration) ───
  // Reads require staff; writes require RBAC edit on the 'exec-reports' view
  // (deletes require the delete permission). Backed by api/functions/gm-exec-reports.ts.
  gmExecReports: {
    reports: {
      list: () => get<import('../types').GmExecReport[]>('/gm/exec-reports'),
      /** Assembles the aggregate metrics snapshot server-side, generates the AI
       *  narrative, and saves the report. Slow call — the model round-trip can
       *  take tens of seconds; the UI shows a busy state. */
      generate: (data: { title?: string; periodStart: string; periodEnd: string; notes?: string }) =>
        post<import('../types').GmExecReport>('/gm/exec-reports/generate', data),
      /** Title / narrative polish only — the metrics snapshot, period, and
       *  generation attribution are immutable server-side. */
      update: (id: string, data: { title?: string; narrative?: string }) =>
        patch<import('../types').GmExecReport>(`/gm/exec-reports/${id}`, data),
      remove: (id: string) => del<{ ok: boolean }>(`/gm/exec-reports/${id}`),
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// TYPE RE-EXPORTS (these come from db/schema.ts in the shared package)
// ═══════════════════════════════════════════════════════════════════════════════
export interface KnowledgeBase {
  id: string; name: string; description: string; teamId: string;
  isDefault: boolean; createdAt: string; updatedAt: string; createdBy: string;
}

export interface Article {
  id: string; categoryId: string; knowledgeBaseId: string; title: string; content: string; status: string;
  tags: string[]; mitreTechniques: string[]; author: string; views: number;
  verifierId: string; verifiedAt: string; reviewCycleDays: number;
  feedback: { up: number; down: number }; relatedArticleIds: string[];
  versions: Array<{ date: string; userId: string; note: string }>;
  createdAt: string; updatedAt: string;
}


export interface TrainingProgress {
  id: string; userId: string; courseId: string;
  status: 'not-started' | 'in-progress' | 'completed';
  completionPercent: number; assessmentScore: number | null;
  moduleProgress: Record<string, { status: string; score: number | null; timeSpentMinutes: number }>;
  lastActiveAt: string; completedAt: string | null; totalTimeMinutes: number;
}

/**
 * GET /api/training/compliance.
 *
 * CORRECTED against what the handler actually returns. `courses` is an ARRAY,
 * not a Record - it has been an array since the per-user rule landed, and this
 * type said otherwise, which is part of why no screen consumed the endpoint.
 */
export interface ComplianceCourseEntry {
  courseId: string;
  title: string;
  /** 'required' = everyone. 'assigned' = given to this person specifically. */
  source: 'required' | 'assigned';
  dueDate: string | null;
  renewalMonths: number | null;
  status: 'not-started' | 'in-progress' | 'completed';
  completionPercent: number;
  assessmentScore: number | null;
  completedAt: string | null;
  /** A past completion that has aged out of its renewal window. */
  expired: boolean;
  overdue: boolean;
}

export interface ComplianceRow {
  userId: string;
  userName: string;
  totalRequired: number;
  completedRequired: number;
  isCompliant: boolean;
  overdueCount: number;
  notStartedCount: number;
  courses: ComplianceCourseEntry[];
}

export interface ComplianceReport {
  /** Every course anyone is on the hook for - the matrix header. */
  requiredCourses: Array<{ id: string; title: string }>;
  compliance: ComplianceRow[];
  /** Non-empty = a container read failed and these numbers read BETTER than
   *  reality. Surface it; do not quietly present a partial report as whole. */
  degraded?: string[];
  summary: {
    totalAnalysts: number;
    compliant: number;
    nonCompliant: number;
    overdue: number;
    notStarted: number;
  };
}

export interface Certificate {
  analyst: string; courseId: string; completedAt: string;
  assessmentScore: number | null; certificateId: string;
}

export interface Project {
  id: string; name: string; icon: string; description: string;
  ownerId: string; status: string; labels: string[];
  dueDate: string; createdAt: string; updatedAt: string;
  /** Kanban column name (see src/config/projectColumns.ts). */
  column?: string;
}

export interface Task {
  id: string; projectId: string | null; title: string; description: string;
  status: string; priority: string; assigneeId: string;
  labels: string[]; dueDate: string | null; createdAt: string;
  completedAt: string | null; sortOrder: number;
}

export interface Comment {
  id: string; taskId: string; userId: string; content: string;
  createdAt: string; reactions: Record<string, string[]>;
}

export interface SearchGap {
  id: string; query: string; count: number; resolved: boolean;
}

export interface Handoff {
  id: string; date: string; shiftFrom: string; shiftTo: string;
  authorId: string; authorName?: string; incidents: Array<{
    id: string; title: string; severity: 1 | 2 | 3 | 4;
    status: 'Active' | 'Contained' | 'Monitoring' | 'Resolved';
    notes: string; customerId?: string; customerName?: string;
  }>; notes: string; createdAt: string; updatedAt?: string; updatedBy?: string;
}

export interface Notification {
  id: string; userId: string; type: string; title: string;
  body: string; read: boolean; createdAt: string;
}

export interface ActivityEntry {
  id: string; userId: string; type: string; description: string;
  createdAt: string;
}

export interface Customer {
  id: string; name: string; m365Tier: string; mdoPlan: string;
  mdePlan: string; sentinelEnabled: boolean; tenantCount: number;
}

// ─── Customer Onboarding (mirrors api/shared/onboardingRuns.ts) ───
export interface OnboardingInput {
  clientName: string; freshServiceID: string;
  workspaceName: string; workspaceID: string; resourceGroupName: string;
  subscriptionID: string; subscriptionName: string; tenantID: string;
  email: string; roeLink: string; monthlyReportLink: string; tvmLink?: string;
  slaHigh?: string; slaMedium?: string; slaLow?: string; slaInfo?: string;
  freqHigh?: string; freqMedium?: string; freqLow?: string; freqInfo?: string;
  escalationHours?: string; escalationPath?: string; enabled?: boolean;
}

export type OnboardingRunStatus =
  | 'pending_approval' | 'approved' | 'running'
  | 'succeeded' | 'partial_failure' | 'failed'
  | 'rejected' | 'cancelled';

export interface OnboardingStepDoc {
  key: string; label: string;
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';
  attempts: number; startedAt?: string; finishedAt?: string;
  lastError?: string; result?: Record<string, unknown>;
}

export interface OnboardingRunDoc {
  id: string; input: OnboardingInput;
  clientId: string; hubCustomerSlug: string; freshPad: string;
  status: OnboardingRunStatus;
  statusHistory: Array<{ from: string; to: string; actorId: string; actorName: string; at: string; reason?: string }>;
  steps: OnboardingStepDoc[];
  socAnnouncement: {
    status: 'not_scheduled' | 'scheduled' | 'sent' | 'failed' | 'cancelled';
    scheduledFor?: string; sentAt?: string; lastError?: string; notes?: string;
  };
  createdBy: string; createdByName: string; createdAt: string;
  approvedBy?: string; approvedByName?: string; approvedAt?: string;
  completedAt?: string; updatedAt: string;
}

export interface OnboardingPreviewResponse {
  valid: boolean;
  problems?: string[];
  derived?: { clientId: string; hubCustomerSlug: string; freshPad: string };
  target?: { subscriptionId: string; resourceGroup: string; workspaceName: string; onboardingEnabled: boolean };
  items?: Array<{ alias: string; itemId: string; itemsKeyValue: Record<string, string> }>;
}

export interface OnboardingDiagnoseResponse {
  target: { subscriptionId: string; resourceGroup: string; workspaceName: string; usingDefaults: boolean };
  onboardingEnabled: boolean;
  socEmailConfigured: boolean;
  tokenOk: boolean;
  probes: Array<{ alias: string; ok: boolean; httpStatus?: number; error?: string }>;
  checks: Array<{ label: string; ok: boolean; value: string }>;
}

export interface TenantWorkspace {
  id: string; name: string; workspaceId: string; subscriptionId: string;
  resourceGroup: string; enabled: boolean;
  lastSyncAt: string | null; lastError: string | null;
  createdAt: string; updatedAt: string;
}

export interface SLATenantMetrics {
  tenantId: string; tenantName: string; totalIncidents: number;
  avgResponseMinutes: number; avgResolutionMinutes: number;
  slaBreachCount: number; bySeverity: { high: number; medium: number; low: number };
  queriedAt: string;
}

export interface SLAMetricsResponse {
  customers: SLATenantMetrics[]; isLive: boolean; queriedAt: string | null;
}

export interface AnalystLiveMetrics {
  analystName: string; totalIncidents: number; resolved: number;
  bySeverity: { high: number; medium: number; low: number };
  byTenant: Array<{ tenantId: string; tenantName: string; count: number }>;
  queriedAt: string;
}

export interface AnalystWorkloadResponse {
  analysts: AnalystLiveMetrics[]; isLive: boolean; queriedAt: string | null;
}

export interface IncidentsSummaryResponse {
  daily: Array<{ date: string; totalIncidents: number; bySeverity: { high: number; medium: number; low: number } }>;
  totalWeek: number; bySeverity: { high: number; medium: number; low: number };
  byCustomer: Array<{ tenantId: string; tenantName: string; count: number }>;
  isLive: boolean; queriedAt: string | null;
}

// Ticket, Escalation, Roster, QA types (mirrors src/types/index.ts shapes)
export interface Ticket {
  id: string; number: string; title: string; description: string;
  type: string; status: string; priority: string; impact: string; urgency: string;
  category: string; subcategory: string; source: string;
  customerId: string | null; requesterId: string; assigneeId: string | null;
  teamId: string | null; watcherIds: string[]; parentTicketId: string | null;
  mergedIntoId: string | null; relatedTicketIds: string[]; linkedArticleIds: string[];
  linkedIncidentId: string | null; tags: string[]; customFields: Record<string, any>;
  sla: Record<string, any>; timeEntries: any[]; totalTimeMinutes: number;
  changeRisk: string; changeReason: string; rollbackPlan: string;
  scheduledStart: string | null; scheduledEnd: string | null;
  approvals: any[]; approvalStatus: string;
  rootCause: string; workaround: string; knownError: boolean; affectedTicketIds: string[];
  timeline: any[]; comments: any[];
  csatScore: number | null; csatComment: string;
  createdAt: string; updatedAt: string; resolvedAt: string | null; closedAt: string | null;
  firstResponseAt: string | null; lastCustomerReplyAt: string | null;
  reopenCount: number; escalationCount: number;
}

export interface IncidentEscalation {
  id: string; ticketId: string | null; title: string; severity: 1 | 2 | 3 | 4;
  status: 'open' | 'acknowledged' | 'resolved';
  assigneeId: string | null; customerId: string | null; customerName: string;
  acknowledgedAt: string | null; acknowledgedBy: string | null;
  resolvedAt: string | null; resolvedBy: string | null;
  escalationCount: number; notes: string; createdAt: string; updatedAt: string;
}

export interface IncidentResponseCase {
  id: string; caseNumber: string; title: string;
  status: 'new' | 'triage' | 'investigation' | 'containment' | 'eradication' | 'recovery' | 'closed';
  severity: 1 | 2 | 3 | 4;
  engagementType: 'mxdr' | 'secureshield365';
  // SecureShield365-specific scope tracking; null for MXDR cases
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  secureShield365Scope: any | null;
  customerName: string; customerId: string | null;
  requesterName: string; requesterEmail: string;
  source: 'manual' | 'freshservice' | 'email';
  freshserviceTicketId: number | null;
  assigneeIds: string[]; leadAnalystId: string | null;
  detectedAt: string | null; reportedAt: string;
  attackVector: string; userImpacted: string; systemsImpacted: string; summary: string;
  investigationNotes: Array<{ id: string; authorId: string; content: string; createdAt: string }>;
  iocs: Array<{ id: string; type: string; value: string; description: string; observedAt: string; addedBy: string; addedAt: string }>;
  timeline: Array<{ id: string; type: string; description: string; occurredAt: string; loggedBy: string; loggedAt: string }>;
  containmentActions: string; eradicationActions: string; recoveryActions: string; lessonsLearned: string;
  createdAt: string; updatedAt: string;
  closedAt: string | null; closedBy: string | null;
}

export interface RosterSlot {
  id: string; zoneId: string; userId: string | null;
  shift: 'Shift 1' | 'Shift 2' | 'Shift 3'; date: string; inactive: boolean;
}

export interface ApiChecklistItem {
  id: string; title: string; description: string;
  category: string; priority: string; applicableShifts: string[];
}

export interface ApiChecklistCompletion {
  id: string; itemId: string; userId: string; shift: string; date: string; completedAt: string;
}

export interface CustomRole {
  id: string; name: string; description: string; color: string;
  permissions: Array<{ viewId: string; label: string; canView: boolean; canEdit: boolean; canDelete: boolean }>;
  createdAt: string; updatedAt: string;
}

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  mobilePhone?: string;
  avatarUrl?: string;
  role: 'admin' | 'lead' | 'analyst' | 'customer';
  /** Customer spaces this user belongs to — only set when role === 'customer'. */
  customerSlugs?: string[];
  tier: 'T1' | 'T2' | 'GM' | 'IC' | 'QA';
  shift: 'Shift 1' | 'Shift 2' | 'Shift 3';
  entraGroups: string[];
  firstLoginAt: string;
  lastLoginAt: string;
  createdAt: string;
  updatedAt: string;
}

/** Slim directory entry returned by /api/users/directory.  No sensitive
 *  fields — safe to load for every authenticated user. */
export interface UserDirectoryEntry {
  id: string;
  displayName: string;
  email: string;
  avatarUrl?: string;
}

export interface UserRoleAssignment {
  userId: string; customRoleId: string; assignedAt: string; assignedBy: string;
}

export interface DecisionTreeRecord {
  id: string; title: string; icon: string; description: string;
  tags: string[]; relatedKbCategories: string[];
  steps: Array<{
    id: string; question: string;
    options?: Array<{ label: string; nextStepId: string }>;
    isResult?: boolean; action?: string; color?: string; kbRef?: string;
  }>;
  status?: 'draft' | 'published' | 'archived';
  source?: 'built-in' | 'ai-generated' | 'manual';
  generatedFrom?: string;
  createdAt?: string; updatedAt?: string; createdBy?: string;
}
